import { PropsWithChildren, useCallback, useMemo, useState } from "react";

import { NotificationsContext } from "./context";
import { Notification, NotificationsSummary } from "./types";
import {
  useApiGetNotifications,
  useApiGetNotificationsSummary,
  useApiUpdateNotification,
} from "@hooks/api/notifications";

export const NotificationsProvider = ({ children }: PropsWithChildren) => {
  const [getNotificationsSummary] = useApiGetNotificationsSummary();
  const [getNotifications] = useApiGetNotifications();
  const [updateNotification] = useApiUpdateNotification();

  const [notifications, setNotificationsState] = useState<Notification[]>([]);
  const [summary, setSummary] = useState<NotificationsSummary>({
    has_pending: false,
    pending_count: 0,
    unread_count: 0,
    pending_acceptance_count: 0,
  });

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const unreadCount = useMemo(
    () => summary.unread_count,
    [summary.unread_count],
  );

  const hasPending = useMemo(
    () =>
      summary.has_pending ||
      summary.unread_count > 0 ||
      summary.pending_acceptance_count > 0,
    [summary],
  );

  const openDrawer = useCallback(() => {
    setIsDrawerOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  const setNotifications = useCallback((data: Notification[]) => {
    setNotificationsState(data);
  }, []);

  const refreshSummary = useCallback(async () => {
    try {
      const { data } = await getNotificationsSummary();
      setSummary(data);
    } catch {
      throw new Error("Erro ao buscar resumo de notificações");
    }
  }, [getNotificationsSummary]);

  const loadNotifications = useCallback(async () => {
    try {
      const { data } = await getNotifications();
      setNotificationsState(data);
      return data;
    } catch {
      throw new Error("Erro ao buscar notificações");
    }
  }, [getNotifications]);

  const markAsViewed = useCallback(
    async (id: string) => {
      try {
        const { data } = await updateNotification({
          url: `/notificacoes/${id}/`,
          data: { visualizar: true },
        });

        setNotificationsState((prev) =>
          prev.map((notification) =>
            notification.id === id
              ? {
                  ...notification,
                  visualizada: data.visualizada,
                }
              : notification,
          ),
        );
      } catch {
        throw new Error("Erro ao atualizar notificação");
      }
    },
    [updateNotification],
  );

  const acceptNotification = useCallback(
    async (id: string) => {
      try {
        const { data } = await updateNotification({
          url: `/notificacoes/${id}/`,
          data: { aceitar: true },
        });

        setNotificationsState((prev) =>
          prev.map((notification) =>
            notification.id === id
              ? {
                  ...notification,
                  aceita: data.aceita,
                  visualizada: data.visualizada,
                }
              : notification,
          ),
        );
      } catch {
        throw new Error("Erro ao atualizar notificação");
      }
    },
    [updateNotification],
  );

  const markAllAsViewed = useCallback(
    async (ids: string[]) => {
      await Promise.allSettled(ids.map((id) => markAsViewed(id)));
    },
    [markAsViewed],
  );

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        summary,
        hasPending,
        unreadCount,
        isDrawerOpen,
        openDrawer,
        closeDrawer,
        setNotifications,
        refreshSummary,
        loadNotifications,
        markAsViewed,
        markAllAsViewed,
        acceptNotification,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};
